/** @flow */

import { takeEvery, takeLatest, put, all, call, select, spawn } from "redux-saga/effects";
import http from "@http";
import moment from "moment";
import config from "@config";
import {
  GetSystemUsers,
  UpdateSystemUser,
  SetSystemUsersSort,
  SetSystemUserStatus,
  SearchEmployees,
  ExportSystemUsers,
  ExportSystemUsersSuccess,
  GetCallbackQueueDashboardHeadlines,
  GetCallbackQueueDashboardClientsSummary,
  ExportCallbackQueueDashboardClientsSummary,
  ExportCallbackQueueDashboardAdvisorsSummary,
  GetCallbackQueueDashboardAdvisorsSummary,
  GetMedicalTriageScripts,
  GetCallScripts,
  UpdateCallScript,
  UpdateMedicalTriageScript,
  GetMedicalTriageSymptoms,
  UpdateMedicalTriageSymptom,
  GetMessagesLoginText,
  UpdateMessagesLoginText,
  GetMessagesReleaseVersion,
  UpdateReleaseNote,
  PostSMSTest,
  PostEmailTest,
  GetClinicalIncidentsRegister,
  GetClinicalIncidentsRegisterExport,
  GetIncidentTypes,
  GetIncidentCauses,
  GetClinicalIncident,
  DeleteClinicalIncident,
  LogNewIncident,
  EditIncident,
  GetCallHandlers,
  GetIncidentRateReportGridData,
  GetIncidentRateReportHeadlines,
  ExportIncidentRateReport,
  GetReleaseNotesDocument,
  GetServiceDeskSettings,
  UpdateServiceDeskSettings,
  GetHealthNotices,
  AddHealthNotice,
  UpdateHealthNotice,
  PublishHealthNotice,
  DeleteHealthNotice,
  CancelHealthNotice,
  GetHealthNotice,
  GetImages,
  UploadImage,
  GetAllergies,
  AddAllergy,
  UpdateAllergy,
  GetConditions,
  AddCondition,
  UpdateCondition,
  GetMedications,
  GetMedicationTypes,
  AddMedication,
  UpdateMedication,
  GetVaccinations,
  AddVaccination,
  UpdateVaccination,
  GetProviders,
  GetProviderLogo,
  AddProvider,
  UpdateProvider,
  GetServiceTypes,
  AddServiceType,
  UpdateServiceType,
  GetWellbeingCategories,
  GetParentAbsenceReasons,
  GetCarePlanActivities,
  GetCarePlanActivityTypes,
  GetCarePlanActivityPeriods,
  GetCarePlanActivityParents,
  GetCarePlanActivityCounters,
  AddCarePlanActivity,
  UpdateCarePlanActivity,
  GetSupportPlanActivities,
  GetSupportPlanActivityCounters,
  AddSupportPlanActivity,
  UpdateSupportPlanActivity,
  GetSupportPlanJobDutyConfigs,
  GetTriageQuestions,
  GetTriageQuestionSymptoms,
  AddTriageQuestion,
  UpdateTriageQuestion,
  GetTriageAdvices,
  GetTriageAdviceSymptoms,
  AddTriageAdvice,
  UpdateTriageAdvice,
} from "@modules/management/constants/actions";
import actionCreators from "@modules/management/actionCreator";
import type {
  SystemUser,
  MedicalTriageScript,
  CallScriptManagementType,
  MedicalTriageSymptomNote,
  HealthNotice,
  ImageLibraryItem,
} from "@modules/management/types";
import type { Action } from "redux-action";
import type { PaginationType } from "@types";
import { transformFilters, OPERATORS } from "@transformers/request";
import { sortToString } from "@transformers/sort";
import {
  selectSystemUsersSort,
  selectSystemUsersPage,
  selectEmployeesSort,
  selectEmployeesPage,
  selectSystemUsersSearch,
  selectAllergiesPage,
  selectConditionsPage,
  selectMedicationsPage,
  selectVaccinationsPage,
  selectProvidersPage,
  selectServiceTypesPage,
  selectCarePlanActivitiesPage,
  selectSupportPlanActivitiesPage,
  selectTriageQuestionsPage,
  selectTriageAdvicesPage,
} from "@modules/management/selectors";
import { getReduxForm } from "@selectors";
import { downloadCsvFile, downloadFile } from "@sagas/utils";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { dataURLtoFile } from "@utils/file";

import callbackAdminSaga from "./callback-admin";
import queueManagementSaga from "./queue-management";
import advisorProfileSaga from "./advisor-profile";
import healthNoticeSchema from "../components/health-notice-add-edit-modal/schema";
import { toFormData } from "@utils/form-data";

export type SystemUserListRequest = $Exact<{
  page: number,
  filters: Object,
}>;

export type SetSystemUserStatusRequest = $Exact<{
  id: number,
  isActive: boolean,
}>;

export type ListResponse<T> = $Exact<{
  content: T,
  paginations: PaginationType,
}>;

function* onGetSystemUsers({
  payload,
}: Action<SystemUserListRequest>): Generator<*, void, { data: ListResponse<Array<SystemUser>> }> {
  const currentPage = yield select(selectSystemUsersPage);
  const page = (payload && payload.page) || currentPage;
  const filters = {
    hasOperatingCompanyUserType: true,
    ...((payload || {}).filters || {}),
  };
  const sort = yield select(selectSystemUsersSort);
  const search = yield select(selectSystemUsersSearch);

  try {
    const response = yield call(http.get, `/api/v1/system-management/user`, {
      params: {
        search: transformFilters(
          { ...search, ...filters },
          {
            last_name: OPERATORS.LIKE,
          },
        ),
        page,
        sort: sortToString(sort),
        limit: config.resultsPerPage,
      },
    });

    yield put(actionCreators.GetSystemUsersSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetSystemUsersError(e.response));
  }
}

function* onSearchEmployees({
  payload,
}: Action<SystemUserListRequest>): Generator<*, void, { data: ListResponse<Array<SystemUser>> }> {
  if (!payload) {
    throw Error("Invalid payload provided");
  }
  const currentPage = yield select(selectEmployeesPage);
  const page = (payload && payload.page) || currentPage;
  const filters = payload.filters || {};
  const sort = yield select(selectEmployeesSort);

  try {
    const response = yield call(http.get, `/api/v1/system-management/user`, {
      params: {
        search: transformFilters(filters, {
          last_name: OPERATORS.LIKE,
        }),
        page,
        sort: sortToString(sort),
        limit: config.resultsPerPage,
      },
    });

    yield put(actionCreators.SearchEmployeesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.SearchEmployeesError(e.response));
  }
}

function* onUpdateSystemUser({
  payload,
}: Action<Object>): Generator<*, void, { data: SystemUser }> {
  try {
    if (!payload || !payload.formData || !payload.successMessage) {
      throw Error("Invalid payload provided");
    }
    const formData = payload.formData;
    const userId = formData.id;
    const successMessage = payload.successMessage;

    const response = yield call(http.put, `/api/v1/system-management/user/${userId}`, formData);

    yield all([
      put(actionCreators.UpdateSystemUserSuccess(response.data)),
      put(actionCreators.GetSystemUsers()),
      put(actionCreators.CloseAddUserModal()),
      put(actionCreators.CloseEditUserModal()),
      put(sendSuccessMessage(successMessage)),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateSystemUserError(e.response));
  }
}

function* onSetSystemUserStatus({
  payload,
}: Action<SetSystemUserStatusRequest>): Generator<*, void, { data: SystemUser }> {
  if (!payload || !payload.id || !payload.isActive) {
    throw Error("Invalid payload provided");
  }
  const userId = payload.id;
  const isActive = payload.isActive;

  try {
    const response = yield call(
      http.post,
      `/api/v1/system-management/user/${userId}/update-status`,
      { isActive },
    );

    yield all([
      put(actionCreators.UpdateSystemUserSuccess(response.data)),
      put(actionCreators.GetSystemUsers()),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateSystemUserError(e.response));
  }
}

function* onExportSystemUsers({ payload }: Action<?Object>): Generator<*, void, any> {
  const filters = { ...(payload || {}).filters, hasOperatingCompanyUserType: true };
  try {
    const response = yield call(http.get, `/api/v1/system-management/user`, {
      params: {
        search: transformFilters(filters),
      },
      headers: {
        Accept: "text/csv",
      },
    });

    yield put(actionCreators.ExportSystemUsersSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.ExportSystemUsersError(e.response));
  }
}

export function* onExportSystemUsersSuccess({ payload }: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "system-users.csv");
}

function* onGetCallbackQueueDashboardHeadlines({ payload }) {
  try {
    const response = yield call(
      http.get,
      `api/v1/contact-centre/callback-queue/dashboard/headlines`,
      {
        params: {
          from_date: payload.fromDate,
          to_date: payload.toDate,
        },
      },
    );
    yield put(actionCreators.GetCallbackQueueDashboardHeadlinesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCallbackQueueDashboardHeadlinesError(e.response));
  }
}

function* onGetCallbackQueueDashboardClientsSummary({ payload }) {
  try {
    const response = yield call(
      http.get,
      `api/v1/contact-centre/callback-queue/dashboard/clients_summary`,
      {
        params: {
          from_date: payload.fromDate,
          to_date: payload.toDate,
        },
      },
    );
    yield put(actionCreators.GetCallbackQueueDashboardClientsSummarySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCallbackQueueDashboardClientsSummaryError(e.response));
  }
}

function* onExportCallbackQueueDashboardClientsSummary({ payload }) {
  try {
    const response = yield call(
      http.get,
      `api/v1/contact-centre/callback-queue/dashboard/clients_summary/export`,
      {
        params: {
          from_date: payload.fromDate,
          to_date: payload.toDate,
        },
      },
    );
    downloadCsvFile(
      response.data,
      `Client Performance Summary (${payload.fromDate} - ${payload.toDate}).csv`,
    );
    yield put(actionCreators.ExportCallbackQueueDashboardClientsSummarySuccess());
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.ExportCallbackQueueDashboardClientsSummaryError(e.response));
  }
}

function* onExportCallbackQueueDashboardAdvisorsSummary({ payload }) {
  try {
    const response = yield call(
      http.get,
      `api/v1/contact-centre/callback-queue/dashboard/advisor_summary/export`,
      {
        params: {
          from_date: payload.fromDate,
          to_date: payload.toDate,
        },
      },
    );
    downloadCsvFile(
      response.data,
      `Advisor Performance Summary (${payload.fromDate} - ${payload.toDate}).csv`,
    );
    yield put(actionCreators.ExportCallbackQueueDashboardAdvisorsSummarySuccess());
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.ExportCallbackQueueDashboardAdvisorsSummaryError(e.response));
  }
}

function* onGetCallbackQueueDashboardAdvisorsSummary({ payload }) {
  try {
    const response = yield call(
      http.get,
      `api/v1/contact-centre/callback-queue/dashboard/advisor_summary`,
      {
        params: {
          from_date: payload.fromDate,
          to_date: payload.toDate,
        },
      },
    );
    yield put(actionCreators.GetCallbackQueueDashboardAdvisorsSummarySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCallbackQueueDashboardAdvisorsSummaryError(e.response));
  }
}

function* onGetMedicalTriageScripts(): Generator<
  *,
  void,
  { data: ListResponse<MedicalTriageScript> },
> {
  try {
    const response = yield call(http.get, `/api/v1/contact-centre/medical-triage-script`);

    yield put(actionCreators.GetMedicalTriageScriptsSuccess(response.data.content));
  } catch (e) {
    console.error("Saga error: ", e);
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMedicalTriageScriptsError(e.response));
  }
}

function* onGetCallScripts(): Generator<*, void, { data: ListResponse<CallScriptManagementType> }> {
  try {
    const response = yield call(http.get, `/api/v1/contact-centre/call-script`);

    yield put(actionCreators.GetCallScriptsSuccess(response.data.content));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCallScriptsError(e.response));
  }
}

function* onGetMedicalTriageSymptoms(): Generator<
  *,
  void,
  { data: ListResponse<MedicalTriageSymptomNote> },
> {
  try {
    const response = yield call(http.get, `/api/v1/contact-centre/symptom`);

    yield put(actionCreators.GetMedicalTriageSymptomsSuccess(response.data.content));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMedicalTriageSymptomsError(e.response));
  }
}

function* onUpdateCallScript({
  payload,
}): Generator<*, void, ListResponse<CallScriptManagementType>> {
  const callScriptId: number = payload.callScriptId;
  const callScript: Object = payload.formData;
  try {
    yield call(http.put, `/api/v1/contact-centre/call-script/${callScriptId}`, callScript);

    yield all([
      put(actionCreators.UpdateCallScriptSuccess()),
      put(actionCreators.ToggleEditCallScriptModal({ visible: false, id: null })),
      put(sendSuccessMessage("CALL_SCRIPT_UPDATED")),
      put(actionCreators.GetCallScripts()),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateCallScriptError(e.response));
  }
}

function* onUpdateMedicalTriageScript({
  payload,
}): Generator<*, void, ListResponse<MedicalTriageScript>> {
  const medicalTriageScriptId: number = payload.medicalTriageScriptId;
  const medicalTriageScript: Object = payload.formData;
  try {
    yield call(
      http.put,
      `/api/v1/contact-centre/medical-triage-script/${medicalTriageScriptId}`,
      medicalTriageScript,
    );

    yield all([
      put(actionCreators.ToggleEditMedicalTriageScriptModal({ visible: false, id: null })),
      put(sendSuccessMessage("MEDICAL_TRIAGE_SCRIPT_UPDATED")),
      put(actionCreators.GetMedicalTriageScripts()),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateMedicalTriageScriptError(e.response));
  }
}

function* onUpdateMedicalTriageSymptom({ payload }): Generator<*, void, MedicalTriageSymptomNote> {
  const symptomId: number = payload.symptomId;
  const symptom: Object = payload.formData;
  try {
    yield call(http.put, `/api/v1/contact-centre/symptom/${symptomId}`, symptom);

    yield all([
      put(actionCreators.UpdateMedicalTriageSymptomSuccess()),
      put(actionCreators.ToggleEditMedicalTriageSymptomModal({ visible: false, id: null })),
      put(sendSuccessMessage("MEDICAL_TRIAGE_NOTE_UPDATED")),
      put(actionCreators.GetMedicalTriageSymptoms()),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateMedicalTriageSymptomError(e.response));
  }
}

function* onGetMessagesLoginText(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/system-management/system-message`);

    yield put(actionCreators.GetMessagesLoginTextSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMessagesLoginTextError(e.response));
  }
}

function* onUpdateMessagesLoginText({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.message) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `api/v1/system-management/system-message`, payload);

    yield put(actionCreators.GetMessagesLoginTextSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMessagesLoginTextError(e.response));
  }
}

function* onGetMessagesReleaseVersion(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/system-management/release-version`);

    yield put(actionCreators.GetMessagesReleaseVersionSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMessagesReleaseVersionError(e.response));
  }
}

function* onUpdateReleaseNote({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.formData) {
      throw Error("Invalid payload provided");
    }

    const file = dataURLtoFile(payload.formData.file);

    // Preparing "multipart/form-data" request
    const data = new FormData();
    data.append("file", file.blob, decodeURIComponent(file.name));
    data.append("version", payload.formData.note);

    const response = yield call(http.post, `api/v1/system-management/release-version`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    yield put(actionCreators.GetMessagesReleaseVersionSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMessagesReleaseVersionError(e.response));
  }
}

function* onGetReleaseNotesDocument({
  payload,
}: Action<{ fileName: string }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.fileName) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.get, `api/v1/system-management/release-version/notes`, {
      responseType: "blob",
    });

    downloadFile(response.data, payload.fileName);

    yield put(actionCreators.GetReleaseNotesDocumentSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetReleaseNotesDocumentError(e.response));
  }
}

//SMS
function* onPostSMSTest({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }
    const data = new FormData();
    data.append("body", payload.body);
    data.append("number", payload.number);

    const response = yield call(http.post, `api/v1/sms/send`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    yield put(actionCreators.PostSMSTestSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.PostSMSTestError(e.response));
  }
}

// EMAIL
function* onPostEmailTest({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }
    const data = new FormData();
    data.append("body", payload.body);
    data.append("subject", payload.subject);
    data.append("to", payload.email);

    const response = yield call(http.post, `api/v1/email/send`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    yield put(actionCreators.PostEmailTestSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.PostEmailTestError(e.response));
  }
}

function* onGetClinicalIncident({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }
    const isEditable = payload.isEditable;
    const response = yield call(
      http.get,
      `/api/v1/clinical-governance/client/${payload.clientId}/employee/${payload.employeeId}/clinical-incidents/${payload.clinicalIncidentId}`,
    );

    yield put(
      actionCreators.ToggleViewEditIncidentModal({
        data: response.data,
        isEditable,
      }),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetClinicalIncidentError(e.response));
  }
}
function* onDeleteClinicalIncident({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }
    const isEditable = payload.isEditable;
    const response = yield call(
      http.delete,
      `/api/v1/clinical-governance/client/${payload.clientId}/employee/${payload.employeeId}/clinical-incidents/${payload.clinicalIncidentId}`,
    );

    yield all([
      put(actionCreators.GetClinicalIncidentsRegister({ isDeleted: false })),
      put(actionCreators.DeleteClinicalIncidentSuccess(response.status)),
    ]);
  } catch (e) {
    console.error("error: ", e);
    yield put(actionCreators.DeleteClinicalIncidentError(e.response));
  }
}

function* onLogNewIncident({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }
    yield call(
      http.post,
      `/api/v1/clinical-governance/client/${payload.clientId}/employee/${payload.employeeId}/clinical-incidents`,
      {
        ...payload.formData,
      },
    );
    const forms = yield select(getReduxForm);
    yield all([
      put(
        actionCreators.GetClinicalIncidentsRegister({
          ...forms["clinical-incident-register-filter"].values,
        }),
      ),
      put(actionCreators.ToggleLogNewIncidentModal()),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.LogNewIncidentError(e.response));
  }
}

function* onEditIncident({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/clinical-governance/client/${payload.clientId}/employee/${payload.employeeId}/clinical-incidents/${payload.formData.id}`,
      {
        ...payload.formData,
      },
    );
    const search = yield select(getReduxForm);
    yield all([
      put(
        actionCreators.GetClinicalIncidentsRegister({
          ...search["clinical-incident-register-filter"].values,
        }),
      ),
      put(actionCreators.ToggleViewEditIncidentModal({ data: null, isEditable: false })),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.LogNewIncidentError(e.response));
  }
}

function* onGetClinicalIncidentsRegister({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      console.error("onGetClinicalIncidentsRegister no payload provided");
      throw Error("Invalid payload provided");
    }

    const {
      fromDate,
      toDate,
      incidentTypeId,
      incidentCauseId,
      clientId,
      callHandlerId,
      status,
      isDeleted,
      page,
    } = payload;

    let transformedFilters = transformFilters(
      {
        incidentTypeId,
        incidentCauseId,
        clientId,
        callHandlerId,
        status,
        isDeleted,
      },
      {
        incidentTypeId: OPERATORS.EQUALS,
        incidentCauseId: OPERATORS.EQUALS,
        clientId: OPERATORS.EQUALS,
        callHandlerId: OPERATORS.EQUALS,
        status: OPERATORS.EQUALS,
        isDeleted: OPERATORS.EQUALS,
      },
    );

    let dateFilter = "";
    if (fromDate) dateFilter += `,incident_date>${fromDate}`;
    if (toDate) dateFilter += `,incident_date<${toDate}`;
    const response = yield call(http.get, `api/v1/clinical-governance/clinical-incidents`, {
      params: {
        page,
        limit: config.extendedResultsPerPage,
        search: transformedFilters + dateFilter,
      },
    });
    yield put(actionCreators.GetClinicalIncidentsRegisterSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetClinicalIncidentsRegisterError(e.response));
  }
}

function* onGetClinicalIncidentsRegisterExport({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload) {
      console.error("onGetClinicalIncidentsRegister no payload provided");
      throw Error("Invalid payload provided");
    }

    const {
      fromDate,
      toDate,
      incidentTypeId,
      incidentCauseId,
      clientId,
      callHandlerId,
      status,
      isDeleted,
    } = payload;

    let transformedFilters = transformFilters(
      {
        incidentTypeId,
        incidentCauseId,
        clientId,
        callHandlerId,
        status,
        isDeleted,
      },
      {
        incidentTypeId: OPERATORS.EQUALS,
        incidentCauseId: OPERATORS.EQUALS,
        clientId: OPERATORS.EQUALS,
        callHandlerId: OPERATORS.EQUALS,
        status: OPERATORS.EQUALS,
        isDeleted: OPERATORS.EQUALS,
      },
    );

    let dateFilter = "";
    if (fromDate) dateFilter += `,incident_date>${fromDate}`;
    if (toDate) dateFilter += `,incident_date<${toDate}`;
    const response = yield call(http.get, `api/v1/clinical-governance/clinical-incidents/export`, {
      params: {
        limit: 2000,
        search: transformedFilters + dateFilter,
      },
    });
    yield downloadCsvFile(response.data, `Clinical Incident Register export.csv`);

    yield put(actionCreators.GetClinicalIncidentsRegisterExportSuccess());
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetClinicalIncidentsRegisterExportError(e.response));
  }
}

function* onGetIncidentTypes(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/data/clinical-incident-types`);
    yield put(actionCreators.GetIncidentTypesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetIncidentTypesError(e.response));
  }
}

function* onGetIncidentCauses(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/data/clinical-incident-causes`);
    yield put(actionCreators.GetIncidentCausesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetIncidentCausesError(e.response));
  }
}

function* onGetIncidentRateReportGridData({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.reportType) {
      console.error("onGetClinicalIncidentsRegister no payload provided");
      throw Error("Invalid payload provided");
    }

    const search = yield select(getReduxForm);
    const formValues = search["incident-rates-report-filter"].values;

    const { incidentCause, incidentType } = formValues;

    const response = yield call(
      http.post,
      `/api/v1/clinical-governance/clinical-incident-rate-report/report`,
      {
        report_type: payload.reportType, // MONTH | CAUSE
        from_date:
          formValues.fromDate && formValues.fromDate !== ""
            ? moment(formValues.fromDate).format(config.dateFormatBE)
            : null,
        to_date:
          formValues.toDate && formValues.toDate !== ""
            ? moment(formValues.toDate).format(config.dateFormatBE)
            : null,
        incident_cause: incidentCause,
        incident_type: incidentType,
      },
    );
    yield put(
      actionCreators.GetIncidentRateReportGridDataSuccess({ [payload.reportType]: response.data }),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetIncidentRateReportGridDataError(e.response));
  }
}

function* onGetIncidentRateReportHeadlines({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.reportType) {
      console.error("onGetClinicalIncidentsRegister no payload provided");
      throw Error("Invalid payload provided");
    }
    const search = yield select(getReduxForm);
    const formValues = search["incident-rates-report-filter"].values;

    const { incidentCause, incidentType } = formValues;

    const response = yield call(
      http.post,
      `/api/v1/clinical-governance/clinical-incident-rate-report/headlines`,
      {
        report_type: payload.reportType, // MONTH | CAUSE
        from_date:
          formValues.fromDate && formValues.fromDate !== ""
            ? moment(formValues.fromDate).format(config.dateFormatBE)
            : null,
        to_date:
          formValues.toDate && formValues.toDate !== ""
            ? moment(formValues.toDate).format(config.dateFormatBE)
            : null,
        incident_cause: incidentCause,
        incident_type: incidentType,
      },
    );
    yield put(actionCreators.GetIncidentRateReportHeadlinesSuccess({ headlines: response.data }));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetIncidentRateReportHeadlinesError(e.response));
  }
}

function* onExportIncidentRateReport({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.reportType) {
      console.error("onGetClinicalIncidentsRegister no payload provided");
      throw Error("Invalid payload provided");
    }
    const search = yield select(getReduxForm);
    const formValues = search["incident-rates-report-filter"].values;

    const { incidentCause, incidentType } = formValues;

    const response = yield call(
      http.post,
      `api/v1/clinical-governance/clinical-incident-rate-report/export`,
      {
        report_type: payload.reportType, // MONTH | CAUSE
        from_date:
          formValues.fromDate && formValues.fromDate !== ""
            ? moment(formValues.fromDate).format(config.dateFormatBE)
            : null,
        to_date:
          formValues.toDate && formValues.toDate !== ""
            ? moment(formValues.toDate).format(config.dateFormatBE)
            : null,
        incident_cause: incidentCause,
        incident_type: incidentType,
      },
    );

    yield downloadCsvFile(response.data, `Incident Rate Report by ${payload.reportType}.csv`);
    yield put(actionCreators.ExportIncidentRateReportSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.ExportIncidentRateReportError(e.response));
  }
}

function* onGetCallHandlers(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `api/v1/employee-admin/client/1/employee`, {
      params: {
        limit: 2000,
        search:
          "user_type:NURSE_ADVISOR,user_type:LEAD_NURSE_ADVISOR,user_type:CONTACT_CENTRE_MANAGER",
      },
    });
    yield put(
      actionCreators.GetCallHandlersSuccess(
        response.data.content.map(itm => {
          return {
            id: itm.id,
            name: `${itm.firstName} ${itm.lastName}`,
          };
        }),
      ),
    );
  } catch (e) {
    yield put(actionCreators.GetCallHandlersError(e.response));
  }
}

function* onGetServiceDeskSettings(): Generator<*, void, *> {
  try {
    const [project, ticketStatuses] = yield all([
      call(http.get, `${config.serviceDeskEndpointBaseUrl}api/management/jira/projects`),
      call(http.get, `${config.serviceDeskEndpointBaseUrl}api/management/jira/ticket-statuses`),
    ]);

    yield put(
      actionCreators.GetServiceDeskSettingsSuccess({
        projects: project.data,
        ticketStatuses: ticketStatuses.data,
      }),
    );
  } catch (e) {
    yield put(actionCreators.GetServiceDeskSettingsError(e.response));
  }
}

type ServiceDeskPayload = $Exact<{
  defaultProjectId: number,
  defaultStatusId: number,
  finalStatusId: number,
}>;

function* onUpdateServiceDeskSettings({
  payload,
}: Action<ServiceDeskPayload>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.defaultProjectId ||
      !payload.defaultStatusId ||
      !payload.finalStatusId
    ) {
      throw Error("Invalid payload provided");
    }

    yield all([
      call(
        http.post,
        `${config.serviceDeskEndpointBaseUrl}api/management/jira/projects/${payload.defaultProjectId}/assign`,
      ),
      call(
        http.post,
        `${config.serviceDeskEndpointBaseUrl}api/management/jira/ticket-statuses/${payload.defaultStatusId}/assign-default`,
      ),
      call(
        http.post,
        `${config.serviceDeskEndpointBaseUrl}api/management/jira/ticket-statuses/${payload.finalStatusId}/assign-final`,
      ),
    ]);

    yield put(actionCreators.UpdateServiceDeskSettingsSuccess());
    yield put(actionCreators.GetServiceDeskSettings());
    yield put(sendSuccessMessage("Settings successfully updated"));
  } catch (e) {
    yield put(actionCreators.UpdateServiceDeskSettingsError(e.response));
  }
}

function* onGetHealthNotices({
  payload,
}: Action<Object>): Generator<*, void, { data: ListResponse<Array<HealthNotice>> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.get, `/api/v1/client/${payload.clientId}/health-notices`, {
      params: {
        sort: sortToString([
          {
            property: "id",
            direction: "desc",
          },
        ]),
      },
    });

    yield put(actionCreators.GetHealthNoticesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetHealthNoticesError(e.response));
  }
}

const schema = () => {
  const _schema = healthNoticeSchema(true, true);
  return {
    ..._schema,
    properties: {
      ..._schema.properties,
      // need to add "file" property which doesn't exist in the form, but the BE expect it
      file: {
        type: ["string", "null"],
        title: "Image",
        format: "data-url",
      },
    },
  };
};

function* onGetHealthNotice({
  payload,
}: Action<Object>): Generator<*, void, { data: ListResponse<Array<HealthNotice>> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/client/${payload.clientId}/health-notices/${payload.id}`,
    );

    yield put(actionCreators.GetHealthNoticeSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetHealthNoticeError(e.response));
  }
}

const healthNoticeData = data => ({
  type: data.type,
  title: data.title,
  message: data.message,
  referenceUrl: data.referenceUrl || "",
  file: data.imageUrl,
});

function* onAddHealthNotice({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const data = payload.data;

    const response = yield call(http.post, `/api/v1/client/${payload.clientId}/health-notices`, {
      type: data.type,
      title: data.title,
      message: data.message,
      referenceUrl: data.referenceUrl || "",
      imageUrl: data.imageUrl,
    });

    yield all([
      put(actionCreators.AddHealthNoticeSuccess(response.data)),
      put(
        actionCreators.GetHealthNotices({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("HEALTH_NOTICE_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddHealthNoticeError(e.response));
  }
}

function* onUpdateHealthNotice({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }
    const { data } = payload;

    const response = yield call(
      http.put,
      `/api/v1/client/${payload.clientId}/health-notices/${payload.data.id}`,
      {
        type: data.type,
        title: data.title,
        message: data.message,
        referenceUrl: data.referenceUrl || "",
        imageUrl: data.imageUrl,
      },
    );

    yield all([
      put(actionCreators.UpdateHealthNoticeSuccess(response.data)),
      put(
        actionCreators.GetHealthNotices({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("HEALTH_NOTICE_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateHealthNoticeError(e.response));
  }
}

function* onPublishHealthNotice({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/client/${payload.clientId}/health-notices/${payload.data.id}?status=PUBLISHED`,
    );

    yield all([
      put(actionCreators.PublishHealthNoticeSuccess(response.data)),
      put(
        actionCreators.GetHealthNotices({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("HEALTH_NOTICE_PUBLISHED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.PublishHealthNoticeError(e.response));
  }
}

function* onCancelHealthNotice({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/client/${payload.clientId}/health-notices/${payload.data.id}?status=CANCELLED`,
    );

    yield all([
      put(actionCreators.CancelHealthNoticeSuccess(response.data)),
      put(
        actionCreators.GetHealthNotices({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("HEALTH_NOTICE_CANCELLED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.CancelHealthNoticeError(e.response));
  }
}

function* onDeleteHealthNotice({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.delete,
      `/api/v1/client/${payload.clientId}/health-notices/${payload.data.id}`,
    );

    yield all([
      put(actionCreators.DeleteHealthNoticeSuccess(response.data)),
      put(
        actionCreators.GetHealthNotices({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("HEALTH_NOTICE_DELETED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.DeleteHealthNoticeError(e.response));
  }
}

function* onGetImages({ payload }: Action<Object>): Generator<*, void, { data: any }> {
  const imagesPerPage = 25;
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const page = payload.page || 1;
    const search = payload.search
      ? transformFilters({ filename: payload.search }, { filename: OPERATORS.LIKE })
      : undefined;

    const response = yield call(http.get, `/api/v1/contact-centre/images`, {
      params: {
        search,
        page,
        limit: imagesPerPage,
      },
    });

    const data: {
      content: ImageLibraryItem[],
      pagination: PaginationType,
      totalElements: number,
    } = Array.isArray(response.data)
      ? {
          content: response.data,
          pagination: {
            sort: { orders: [] },
            pageNumber: page,
            pageSize: imagesPerPage,
            offset: 0,
          },
          totalElements: response.data.length,
        }
      : response.data;

    yield put(actionCreators.GetImagesSuccess(data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetImagesError(e.response));
  }
}

function* onUploadImage({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const form = toFormData(
      {
        type: "object",
        properties: {
          filename: { type: "string", title: "Name" },
          image: { type: ["string", "null"], title: "Image", format: "data-url" },
        },
      },
      payload.data,
    );

    yield call(http.post, `/api/v1/contact-centre/image/upload`, form, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    yield all([
      put(actionCreators.UploadImageSuccess()),
      put(actionCreators.GetImages({ clientId: payload.clientId })),
      put(sendSuccessMessage("IMAGE_UPLOADED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UploadImageError(e.response));
  }
}

function* onGetAllergies({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectAllergiesPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(http.get, `/api/v1/data/classification/allergy/overview`, {
      params: {
        page,
        limit: config.resultsPerPage,
      },
    });

    const { data } = response;
    const content = data.content || [];

    yield put(actionCreators.GetAllergiesSuccess({ ...data, content }));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetAllergiesError(e.response));
  }
}

function* onAddAllergy({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.name) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `/api/v1/data/classification/allergy`, {
      name: payload.name,
    });

    yield all([
      put(actionCreators.AddAllergySuccess(response.data)),
      put(actionCreators.SetAllergyModal({ visible: false, data: null })),
      put(actionCreators.GetAllergies()),
      put(sendSuccessMessage("ALLERGY_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddAllergyError(e.response));
  }
}

function* onUpdateAllergy({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.id || !payload.name) {
      throw Error("Invalid payload provided");
    }

    yield call(http.put, `/api/v1/data/classification/allergy/${payload.id}`, {
      id: payload.id,
      name: payload.name,
    });

    yield all([
      put(actionCreators.UpdateAllergySuccess()),
      put(actionCreators.SetAllergyModal({ visible: false, data: null })),
      put(actionCreators.GetAllergies()),
      put(sendSuccessMessage("ALLERGY_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateAllergyError(e.response));
  }
}

function* onGetConditions({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectConditionsPage);
  const page = (payload && payload.page) || currentPage;
  const keyword = (payload && payload.search && payload.search.trim()) || "";

  try {
    const response = yield call(http.get, `/api/v1/data/classification/condition/overview`, {
      params: {
        page,
        limit: config.resultsPerPage,
        ...(keyword ? { search: `name:${keyword}` } : {}),
      },
    });

    const { data } = response;
    const content = data.content || [];

    yield put(actionCreators.GetConditionsSuccess({ ...data, content }));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetConditionsError(e.response));
  }
}

function* onAddCondition({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.name) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `/api/v1/data/classification/condition`, {
      name: payload.name,
    });

    yield all([
      put(actionCreators.AddConditionSuccess(response.data)),
      put(actionCreators.SetConditionModal({ visible: false, data: null })),
      put(actionCreators.GetConditions(payload.filter)),
      put(sendSuccessMessage("CONDITION_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddConditionError(e.response));
  }
}

function* onUpdateCondition({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.id || !payload.name) {
      throw Error("Invalid payload provided");
    }

    yield call(http.put, `/api/v1/data/classification/condition/${payload.id}`, {
      id: payload.id,
      name: payload.name,
    });

    yield all([
      put(actionCreators.UpdateConditionSuccess()),
      put(actionCreators.SetConditionModal({ visible: false, data: null })),
      put(actionCreators.GetConditions(payload.filter)),
      put(sendSuccessMessage("CONDITION_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateConditionError(e.response));
  }
}

function* onGetMedications({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectMedicationsPage);
  const page = (payload && payload.page) || currentPage;
  const keyword = (payload && payload.search && payload.search.trim()) || "";

  try {
    const response = yield call(http.get, `/api/v1/data/classification/medication/overview`, {
      params: {
        page,
        limit: config.resultsPerPage,
        ...(keyword ? { search: `name:${keyword}` } : {}),
      },
    });

    const { data } = response;
    const content = data.content || [];

    yield put(actionCreators.GetMedicationsSuccess({ ...data, content }));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMedicationsError(e.response));
  }
}

function* onGetMedicationTypes(): Generator<*, void, { data: string[] }> {
  try {
    const response = yield call(http.get, `/api/v1/data/classification/medication/type`);

    yield put(actionCreators.GetMedicationTypesSuccess(response.data || []));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetMedicationTypesError(e.response));
  }
}

function* onAddMedication({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.name || !payload.type) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `/api/v1/data/classification/medication`, {
      name: payload.name,
      type: payload.type,
    });

    yield all([
      put(actionCreators.AddMedicationSuccess(response.data)),
      put(actionCreators.SetMedicationModal({ visible: false, data: null })),
      put(actionCreators.GetMedications(payload.filter)),
      put(sendSuccessMessage("MEDICATION_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddMedicationError(e.response));
  }
}

function* onUpdateMedication({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.id || !payload.name || !payload.type) {
      throw Error("Invalid payload provided");
    }

    yield call(http.put, `/api/v1/data/classification/medication/${payload.id}`, {
      id: payload.id,
      name: payload.name,
      type: payload.type,
    });

    yield all([
      put(actionCreators.UpdateMedicationSuccess()),
      put(actionCreators.SetMedicationModal({ visible: false, data: null })),
      put(actionCreators.GetMedications(payload.filter)),
      put(sendSuccessMessage("MEDICATION_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateMedicationError(e.response));
  }
}

function* onGetProviders({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectProvidersPage);
  const page = (payload && payload.page) || currentPage;
  const status = payload && payload.status;

  let statusSearch = "";
  if (status === "Active") {
    statusSearch = "status:true";
  } else if (status === "Inactive") {
    statusSearch = "status:false";
  }

  try {
    const response = yield call(http.get, `/api/v1/data/classification/provider/overview`, {
      params: {
        page,
        limit: config.resultsPerPage,
        ...(statusSearch ? { search: statusSearch } : {}),
      },
    });

    const { data } = response;
    const content = data.content || [];

    yield put(actionCreators.GetProvidersSuccess({ ...data, content }));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetProvidersError(e.response));
  }
}

function* onGetProviderLogo({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  if (!payload || !payload.id) {
    return;
  }

  try {
    const response = yield call(http.get, `/api/v1/data/classification/provider/${payload.id}`);

    yield put(actionCreators.GetProviderLogoSuccess(response.data && response.data.url));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetProviderLogoError(e.response));
  }
}

function* uploadProviderLogo(id: number, file: string): Generator<*, void, *> {
  const parsed = dataURLtoFile(file);
  const data = new FormData();
  data.append("file", parsed.blob, decodeURIComponent(parsed.name));

  yield call(http.post, `/api/v1/data/classification/provider/${id}/logo`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

function* onAddProvider({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.name || typeof payload.status !== "boolean" || !payload.file) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `/api/v1/data/classification/provider`, {
      name: payload.name,
      status: payload.status,
    });

    const created = response.data && response.data.id;
    const newId = created && typeof created === "object" ? created.id : created;

    yield call(uploadProviderLogo, newId, payload.file);

    yield all([
      put(actionCreators.AddProviderSuccess(response.data)),
      put(actionCreators.SetProviderModal({ visible: false, data: null })),
      put(actionCreators.GetProviders(payload.filter)),
      put(sendSuccessMessage("PROVIDER_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddProviderError(e.response));
  }
}

function* onUpdateProvider({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.id || !payload.name || typeof payload.status !== "boolean") {
      throw Error("Invalid payload provided");
    }

    yield call(http.put, `/api/v1/data/classification/provider/${payload.id}`, {
      id: payload.id,
      name: payload.name,
      status: payload.status,
    });

    if (payload.file && payload.file.startsWith("data:")) {
      yield call(uploadProviderLogo, payload.id, payload.file);
    }

    yield all([
      put(actionCreators.UpdateProviderSuccess()),
      put(actionCreators.SetProviderModal({ visible: false, data: null })),
      put(actionCreators.GetProviders(payload.filter)),
      put(sendSuccessMessage("PROVIDER_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateProviderError(e.response));
  }
}

function* onGetServiceTypes({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectServiceTypesPage);
  const page = (payload && payload.page) || currentPage;
  const status = payload && payload.status;

  let statusSearch = "";
  if (status === "Active") {
    statusSearch = "status:true";
  } else if (status === "Inactive") {
    statusSearch = "status:false";
  }

  try {
    const response = yield call(http.get, `/api/v1/data/classification/service-type`, {
      params: {
        page,
        limit: config.resultsPerPage,
        ...(statusSearch ? { search: statusSearch } : {}),
      },
    });

    const { data } = response;
    const content = data.content || [];

    yield put(actionCreators.GetServiceTypesSuccess({ ...data, content }));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetServiceTypesError(e.response));
  }
}

function* onGetWellbeingCategories(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(
      http.get,
      `/api/v2/service-admin/service-config/absence-management/wellbeing/category`,
    );

    yield put(actionCreators.GetWellbeingCategoriesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetWellbeingCategoriesError(e.response));
  }
}

function* onGetParentAbsenceReasons(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(http.get, `/api/v1/data/parent-absence-reason`);

    yield put(actionCreators.GetParentAbsenceReasonsSuccess((response.data || {}).content || []));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetParentAbsenceReasonsError(e.response));
  }
}

function* onAddServiceType({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.name ||
      typeof payload.status !== "boolean" ||
      !payload.wellbeingCategory
    ) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `/api/v1/data/classification/service-type`, {
      name: payload.name,
      status: payload.status,
      wellbeingCategory: payload.wellbeingCategory,
      absenceReasonParents: payload.absenceReasonParents || [],
    });

    yield all([
      put(actionCreators.AddServiceTypeSuccess(response.data)),
      put(actionCreators.SetServiceTypeModal({ visible: false, data: null })),
      put(actionCreators.GetServiceTypes(payload.filter)),
      put(sendSuccessMessage("SERVICE_TYPE_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddServiceTypeError(e.response));
  }
}

function* onUpdateServiceType({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.id ||
      !payload.name ||
      typeof payload.status !== "boolean" ||
      !payload.wellbeingCategory
    ) {
      throw Error("Invalid payload provided");
    }

    yield call(http.put, `/api/v1/data/classification/service-type/${payload.id}`, {
      id: payload.id,
      name: payload.name,
      status: payload.status,
      wellbeingCategory: payload.wellbeingCategory,
      absenceReasonParents: payload.absenceReasonParents || [],
    });

    yield all([
      put(actionCreators.UpdateServiceTypeSuccess()),
      put(actionCreators.SetServiceTypeModal({ visible: false, data: null })),
      put(actionCreators.GetServiceTypes(payload.filter)),
      put(sendSuccessMessage("SERVICE_TYPE_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateServiceTypeError(e.response));
  }
}

function* onGetVaccinations({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectVaccinationsPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(http.get, `/api/v1/data/classification/vaccine/overview`, {
      params: {
        page,
        limit: config.resultsPerPage,
      },
    });

    const { data } = response;
    const content = data.content || [];

    yield put(actionCreators.GetVaccinationsSuccess({ ...data, content }));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetVaccinationsError(e.response));
  }
}

function* onAddVaccination({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.name) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `/api/v1/data/classification/vaccine`, {
      name: payload.name,
    });

    yield all([
      put(actionCreators.AddVaccinationSuccess(response.data)),
      put(actionCreators.SetVaccinationModal({ visible: false, data: null })),
      put(actionCreators.GetVaccinations()),
      put(sendSuccessMessage("VACCINATION_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddVaccinationError(e.response));
  }
}

function* onUpdateVaccination({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.id || !payload.name) {
      throw Error("Invalid payload provided");
    }

    yield call(http.put, `/api/v1/data/classification/vaccine/${payload.id}`, {
      id: payload.id,
      name: payload.name,
    });

    yield all([
      put(actionCreators.UpdateVaccinationSuccess()),
      put(actionCreators.SetVaccinationModal({ visible: false, data: null })),
      put(actionCreators.GetVaccinations()),
      put(sendSuccessMessage("VACCINATION_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateVaccinationError(e.response));
  }
}

const toCarePlanActivityRequest = (payload: Object) => ({
  activity: payload.activity,
  activityType: payload.activityType,
  timeOfDay: payload.timeOfDay,
  parentActivity: payload.parentActivity || null,
  videoUrl: payload.videoUrl || null,
  isDefault: true,
  imageUrl: payload.imageUrl || null,
  ...(typeof payload.status === "boolean" ? { status: payload.status } : {}),
});

function* onGetCarePlanActivities({
  payload,
}: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectCarePlanActivitiesPage);
  const page = (payload && payload.page) || currentPage;
  const masterAbsenceReasonParentId = payload && payload.masterAbsenceReasonParentId;

  try {
    if (!masterAbsenceReasonParentId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/care-plan-activity`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetCarePlanActivitiesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCarePlanActivitiesError(e.response));
  }
}

function* onGetCarePlanActivityTypes(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(http.get, `/api/v1/data/intervention/care-plan-activity/type`);

    yield put(actionCreators.GetCarePlanActivityTypesSuccess(response.data || []));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCarePlanActivityTypesError(e.response));
  }
}

function* onGetCarePlanActivityPeriods(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(http.get, `/api/v1/data/intervention/care-plan-activity/period`);

    yield put(actionCreators.GetCarePlanActivityPeriodsSuccess(response.data || []));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCarePlanActivityPeriodsError(e.response));
  }
}

function* onGetCarePlanActivityParents(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(http.get, `/api/v1/data/intervention/care-plan-activity/parent`);

    yield put(actionCreators.GetCarePlanActivityParentsSuccess(response.data || []));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCarePlanActivityParentsError(e.response));
  }
}

function* onGetCarePlanActivityCounters({
  payload,
}: Action<Object>): Generator<*, void, { data: Object }> {
  const masterAbsenceReasonParentId = payload && payload.masterAbsenceReasonParentId;

  try {
    if (!masterAbsenceReasonParentId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/care-plan-activity/overview`,
    );

    yield put(
      actionCreators.GetCarePlanActivityCountersSuccess(
        (response.data || {}).activityCounters || [],
      ),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetCarePlanActivityCountersError(e.response));
  }
}

function* onAddCarePlanActivity({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const masterAbsenceReasonParentId =
      payload && payload.masterAbsenceReasonParent && payload.masterAbsenceReasonParent.id;

    if (
      !payload ||
      !payload.activity ||
      !payload.activityType ||
      !payload.timeOfDay ||
      !masterAbsenceReasonParentId
    ) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/care-plan-activity`,
      toCarePlanActivityRequest(payload),
    );

    yield all([
      put(actionCreators.AddCarePlanActivitySuccess(response.data)),
      put(actionCreators.SetCarePlanActivityModal({ visible: false, data: null })),
      put(actionCreators.GetCarePlanActivities(payload.filter)),
      put(actionCreators.GetCarePlanActivityParents()),
      put(actionCreators.GetCarePlanActivityCounters(payload.filter)),
      put(sendSuccessMessage("CARE_PLAN_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddCarePlanActivityError(e.response));
  }
}

function* onUpdateCarePlanActivity({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const masterAbsenceReasonParentId =
      payload && payload.masterAbsenceReasonParent && payload.masterAbsenceReasonParent.id;

    if (
      !payload ||
      !payload.id ||
      !payload.activity ||
      !payload.activityType ||
      !payload.timeOfDay ||
      !masterAbsenceReasonParentId
    ) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/care-plan-activity/${payload.id}`,
      {
        id: payload.id,
        ...toCarePlanActivityRequest(payload),
      },
    );

    yield all([
      put(actionCreators.UpdateCarePlanActivitySuccess()),
      put(actionCreators.SetCarePlanActivityModal({ visible: false, data: null })),
      put(actionCreators.GetCarePlanActivities(payload.filter)),
      put(actionCreators.GetCarePlanActivityParents()),
      put(actionCreators.GetCarePlanActivityCounters(payload.filter)),
      put(sendSuccessMessage("CARE_PLAN_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateCarePlanActivityError(e.response));
  }
}

const toSupportPlanActivityRequest = (payload: Object) => ({
  activityType: payload.activityType,
  dayNum: payload.dayNum,
  guidance: payload.guidance,
  imgUrl: payload.imgUrl || null,
  addOns: payload.addOns || [],
  ...(typeof payload.status === "boolean" ? { status: payload.status } : {}),
});

function* onGetSupportPlanActivities({
  payload,
}: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectSupportPlanActivitiesPage);
  const page = (payload && payload.page) || currentPage;
  const masterAbsenceReasonParentId = payload && payload.masterAbsenceReasonParentId;

  try {
    if (!masterAbsenceReasonParentId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/post-absence-support/activity`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetSupportPlanActivitiesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetSupportPlanActivitiesError(e.response));
  }
}

function* onGetSupportPlanActivityCounters({
  payload,
}: Action<Object>): Generator<*, void, { data: Object }> {
  const masterAbsenceReasonParentId = payload && payload.masterAbsenceReasonParentId;

  try {
    if (!masterAbsenceReasonParentId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/post-absence-support/activity/overview`,
    );

    yield put(
      actionCreators.GetSupportPlanActivityCountersSuccess(
        (response.data || {}).activityCounters || [],
      ),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetSupportPlanActivityCountersError(e.response));
  }
}

function* onAddSupportPlanActivity({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const masterAbsenceReasonParentId =
      payload && payload.masterAbsenceReasonParent && payload.masterAbsenceReasonParent.id;

    if (
      !payload ||
      !payload.activityType ||
      !payload.dayNum ||
      !payload.guidance ||
      !masterAbsenceReasonParentId
    ) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/post-absence-support/activity`,
      toSupportPlanActivityRequest(payload),
    );

    yield all([
      put(actionCreators.AddSupportPlanActivitySuccess(response.data)),
      put(actionCreators.SetSupportPlanActivityModal({ visible: false, data: null })),
      put(actionCreators.GetSupportPlanActivities(payload.filter)),
      put(actionCreators.GetSupportPlanActivityCounters(payload.filter)),
      put(sendSuccessMessage("SUPPORT_PLAN_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddSupportPlanActivityError(e.response));
  }
}

function* onUpdateSupportPlanActivity({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const masterAbsenceReasonParentId =
      payload && payload.masterAbsenceReasonParent && payload.masterAbsenceReasonParent.id;

    if (
      !payload ||
      !payload.id ||
      !payload.activityType ||
      !payload.dayNum ||
      !payload.guidance ||
      !masterAbsenceReasonParentId
    ) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/data/intervention/absence-reason-parent/${masterAbsenceReasonParentId}/post-absence-support/activity/${payload.id}`,
      toSupportPlanActivityRequest(payload),
    );

    yield all([
      put(actionCreators.UpdateSupportPlanActivitySuccess()),
      put(actionCreators.SetSupportPlanActivityModal({ visible: false, data: null })),
      put(actionCreators.GetSupportPlanActivities(payload.filter)),
      put(actionCreators.GetSupportPlanActivityCounters(payload.filter)),
      put(sendSuccessMessage("SUPPORT_PLAN_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateSupportPlanActivityError(e.response));
  }
}

function* onGetSupportPlanJobDutyConfigs(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(
      http.get,
      `/api/v1/data/intervention/post-absence-support/job-duty-config`,
    );

    yield put(
      actionCreators.GetSupportPlanJobDutyConfigsSuccess(
        (response.data || []).sort((a, b) => a.order - b.order),
      ),
    );
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetSupportPlanJobDutyConfigsError(e.response));
  }
}

function* onGetTriageQuestions({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectTriageQuestionsPage);
  const page = (payload && payload.page) || currentPage;
  const symptomId = payload && payload.symptomId;

  try {
    if (!symptomId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/data/intervention/medical-triage/symptom/${symptomId}/question`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetTriageQuestionsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetTriageQuestionsError(e.response));
  }
}

function* onGetTriageQuestionSymptoms(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(http.get, `/api/v1/medical-triage/medical-triage-config`);

    const symptoms = ((response.data || {}).symptoms || []).map(symptom => ({
      id: symptom.id,
      symptomText: symptom.symptomText,
    }));

    yield put(actionCreators.GetTriageQuestionSymptomsSuccess(symptoms));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetTriageQuestionSymptomsError(e.response));
  }
}

function* onAddTriageQuestion({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const symptomId = payload && payload.symptomId;

    if (!payload || !payload.firstLevelQuestion || !symptomId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/data/intervention/medical-triage/symptom/${symptomId}/question`,
      {
        firstLevelQuestion: payload.firstLevelQuestion,
        secondLevelQuestion: payload.secondLevelQuestion || null,
        isRedFlag: payload.isRedFlag,
      },
    );

    yield all([
      put(actionCreators.AddTriageQuestionSuccess(response.data)),
      put(actionCreators.SetTriageQuestionModal({ visible: false, data: null })),
      put(actionCreators.GetTriageQuestions(payload.filter)),
      put(sendSuccessMessage("TRIAGE_QUESTION_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddTriageQuestionError(e.response));
  }
}

function* onUpdateTriageQuestion({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const symptomId = payload && payload.symptomId;

    if (!payload || !payload.id || !payload.firstLevelQuestion || !symptomId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/data/intervention/medical-triage/symptom/${symptomId}/question/${payload.id}`,
      {
        firstLevelQuestion: payload.firstLevelQuestion,
        secondLevelQuestion: payload.secondLevelQuestion || "",
        isRedFlag: payload.isRedFlag,
        status: payload.status,
      },
    );

    yield all([
      put(actionCreators.UpdateTriageQuestionSuccess()),
      put(actionCreators.SetTriageQuestionModal({ visible: false, data: null })),
      put(actionCreators.GetTriageQuestions(payload.filter)),
      put(sendSuccessMessage("TRIAGE_QUESTION_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateTriageQuestionError(e.response));
  }
}

function* onGetTriageAdvices({ payload }: Action<Object>): Generator<*, void, { data: Object }> {
  const currentPage = yield select(selectTriageAdvicesPage);
  const page = (payload && payload.page) || currentPage;
  const symptomId = payload && payload.symptomId;

  try {
    if (!symptomId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/data/intervention/medical-triage/symptom/${symptomId}/advice`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetTriageAdvicesSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetTriageAdvicesError(e.response));
  }
}

function* onGetTriageAdviceSymptoms(): Generator<*, void, { data: Object }> {
  try {
    const response = yield call(http.get, `/api/v1/contact-centre/symptom`);

    const symptoms = ((response.data || {}).content || []).map(symptom => ({
      id: symptom.id,
      symptomText: symptom.symptomText,
    }));

    yield put(actionCreators.GetTriageAdviceSymptomsSuccess(symptoms));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetTriageAdviceSymptomsError(e.response));
  }
}

function* onAddTriageAdvice({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const symptomId = payload && payload.symptomId;

    if (!payload || !payload.adviceText || !symptomId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/data/intervention/medical-triage/symptom/${symptomId}/advice`,
      {
        adviceText: payload.adviceText,
      },
    );

    yield all([
      put(actionCreators.AddTriageAdviceSuccess(response.data)),
      put(actionCreators.SetTriageAdviceModal({ visible: false, data: null })),
      put(actionCreators.GetTriageAdvices(payload.filter)),
      put(sendSuccessMessage("TRIAGE_ADVICE_CREATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.AddTriageAdviceError(e.response));
  }
}

function* onUpdateTriageAdvice({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    const symptomId = payload && payload.symptomId;

    if (!payload || !payload.id || !payload.adviceText || !symptomId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/data/intervention/medical-triage/symptom/${symptomId}/advice/${payload.id}`,
      {
        adviceText: payload.adviceText,
      },
    );

    yield all([
      put(actionCreators.UpdateTriageAdviceSuccess()),
      put(actionCreators.SetTriageAdviceModal({ visible: false, data: null })),
      put(actionCreators.GetTriageAdvices(payload.filter)),
      put(sendSuccessMessage("TRIAGE_ADVICE_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateTriageAdviceError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetSystemUsers, onGetSystemUsers),
    takeLatest(UpdateSystemUser, onUpdateSystemUser),
    takeEvery(SetSystemUsersSort, onGetSystemUsers),
    takeLatest(SetSystemUserStatus, onSetSystemUserStatus),
    takeEvery(SearchEmployees, onSearchEmployees),
    takeEvery(ExportSystemUsers, onExportSystemUsers),
    takeEvery(ExportSystemUsersSuccess, onExportSystemUsersSuccess),
    takeEvery(GetCallbackQueueDashboardHeadlines, onGetCallbackQueueDashboardHeadlines),
    takeEvery(GetCallbackQueueDashboardClientsSummary, onGetCallbackQueueDashboardClientsSummary),
    takeEvery(
      ExportCallbackQueueDashboardClientsSummary,
      onExportCallbackQueueDashboardClientsSummary,
    ),
    takeEvery(
      ExportCallbackQueueDashboardAdvisorsSummary,
      onExportCallbackQueueDashboardAdvisorsSummary,
    ),
    takeEvery(GetCallbackQueueDashboardAdvisorsSummary, onGetCallbackQueueDashboardAdvisorsSummary),
    // Callback Admin
    spawn(callbackAdminSaga),
    // Queue Management
    spawn(queueManagementSaga),
    // Advisor Profile
    spawn(advisorProfileSaga),
    // Script Management
    takeEvery(GetMedicalTriageScripts, onGetMedicalTriageScripts),
    takeEvery(GetCallScripts, onGetCallScripts),
    takeEvery(UpdateCallScript, onUpdateCallScript),
    takeEvery(UpdateMedicalTriageScript, onUpdateMedicalTriageScript),
    takeEvery(GetMedicalTriageSymptoms, onGetMedicalTriageSymptoms),
    takeEvery(UpdateMedicalTriageSymptom, onUpdateMedicalTriageSymptom),
    takeEvery(GetMessagesLoginText, onGetMessagesLoginText),
    takeEvery(UpdateMessagesLoginText, onUpdateMessagesLoginText),
    takeEvery(GetMessagesReleaseVersion, onGetMessagesReleaseVersion),
    takeEvery(UpdateReleaseNote, onUpdateReleaseNote),
    takeEvery(GetReleaseNotesDocument, onGetReleaseNotesDocument),
    takeEvery(PostSMSTest, onPostSMSTest),
    takeEvery(PostEmailTest, onPostEmailTest),
    takeEvery(GetClinicalIncidentsRegister, onGetClinicalIncidentsRegister),
    takeEvery(GetClinicalIncidentsRegisterExport, onGetClinicalIncidentsRegisterExport),
    takeEvery(GetClinicalIncident, onGetClinicalIncident),
    takeEvery(DeleteClinicalIncident, onDeleteClinicalIncident),
    takeEvery(LogNewIncident, onLogNewIncident),
    takeEvery(EditIncident, onEditIncident),
    takeEvery(GetIncidentTypes, onGetIncidentTypes),
    takeEvery(GetIncidentCauses, onGetIncidentCauses),
    takeEvery(GetCallHandlers, onGetCallHandlers),
    takeEvery(GetIncidentRateReportGridData, onGetIncidentRateReportGridData),
    takeEvery(GetIncidentRateReportHeadlines, onGetIncidentRateReportHeadlines),
    takeEvery(ExportIncidentRateReport, onExportIncidentRateReport),
    takeEvery(GetServiceDeskSettings, onGetServiceDeskSettings),
    takeEvery(UpdateServiceDeskSettings, onUpdateServiceDeskSettings),
    takeEvery(GetHealthNotices, onGetHealthNotices),
    takeEvery(AddHealthNotice, onAddHealthNotice),
    takeEvery(UpdateHealthNotice, onUpdateHealthNotice),
    takeEvery(CancelHealthNotice, onCancelHealthNotice),
    takeEvery(PublishHealthNotice, onPublishHealthNotice),
    takeEvery(DeleteHealthNotice, onDeleteHealthNotice),
    takeEvery(GetHealthNotice, onGetHealthNotice),
    // Image Library
    takeEvery(GetImages, onGetImages),
    takeEvery(UploadImage, onUploadImage),
    takeEvery(GetAllergies, onGetAllergies),
    takeEvery(AddAllergy, onAddAllergy),
    takeEvery(UpdateAllergy, onUpdateAllergy),
    takeEvery(GetConditions, onGetConditions),
    takeEvery(AddCondition, onAddCondition),
    takeEvery(UpdateCondition, onUpdateCondition),
    takeEvery(GetMedications, onGetMedications),
    takeEvery(GetMedicationTypes, onGetMedicationTypes),
    takeEvery(AddMedication, onAddMedication),
    takeEvery(UpdateMedication, onUpdateMedication),
    takeEvery(GetProviders, onGetProviders),
    takeEvery(GetProviderLogo, onGetProviderLogo),
    takeEvery(AddProvider, onAddProvider),
    takeEvery(UpdateProvider, onUpdateProvider),
    takeEvery(GetServiceTypes, onGetServiceTypes),
    takeEvery(GetWellbeingCategories, onGetWellbeingCategories),
    takeEvery(GetParentAbsenceReasons, onGetParentAbsenceReasons),
    takeEvery(AddServiceType, onAddServiceType),
    takeEvery(UpdateServiceType, onUpdateServiceType),
    takeEvery(GetVaccinations, onGetVaccinations),
    takeEvery(AddVaccination, onAddVaccination),
    takeEvery(UpdateVaccination, onUpdateVaccination),
    takeEvery(GetCarePlanActivities, onGetCarePlanActivities),
    takeEvery(GetCarePlanActivityTypes, onGetCarePlanActivityTypes),
    takeEvery(GetCarePlanActivityPeriods, onGetCarePlanActivityPeriods),
    takeEvery(GetCarePlanActivityParents, onGetCarePlanActivityParents),
    takeEvery(GetCarePlanActivityCounters, onGetCarePlanActivityCounters),
    takeEvery(AddCarePlanActivity, onAddCarePlanActivity),
    takeEvery(UpdateCarePlanActivity, onUpdateCarePlanActivity),
    takeEvery(GetSupportPlanActivities, onGetSupportPlanActivities),
    takeEvery(GetSupportPlanActivityCounters, onGetSupportPlanActivityCounters),
    takeEvery(AddSupportPlanActivity, onAddSupportPlanActivity),
    takeEvery(UpdateSupportPlanActivity, onUpdateSupportPlanActivity),
    takeEvery(GetSupportPlanJobDutyConfigs, onGetSupportPlanJobDutyConfigs),
    takeEvery(GetTriageQuestions, onGetTriageQuestions),
    takeEvery(GetTriageQuestionSymptoms, onGetTriageQuestionSymptoms),
    takeEvery(AddTriageQuestion, onAddTriageQuestion),
    takeEvery(UpdateTriageQuestion, onUpdateTriageQuestion),
    takeEvery(GetTriageAdvices, onGetTriageAdvices),
    takeEvery(GetTriageAdviceSymptoms, onGetTriageAdviceSymptoms),
    takeEvery(AddTriageAdvice, onAddTriageAdvice),
    takeEvery(UpdateTriageAdvice, onUpdateTriageAdvice),
  ]);
}
